import Error from "next/error";
import { Targeting } from "@foundation/sparkle/lib/AdTargeting/TargetingProvider";
import React, { ComponentProps, useCallback, useMemo } from "react";
import AdOptionsProvider from "@foundation/sparkle/lib/AdOptionsProvider";
import { RegUIClientProvider } from "@foundation/reg-ui-client";
import WinSparkleProvider from "@foundation/sparkle/lib/WinSparkleProvider";
import { StatsigProvider, useStatsigClientWebSetup } from "@foundation/statsig";
import { ContainerWithParallaxLeaderboard } from "@bloomberg/media-ui-ads";
import { TickerBarWithPillWrapper } from "@bloomberg/media-ui-markets";
import { Tophat, type TophatSnippet } from "@foundation/nextjs-tophat";
import { NavProvider } from "@foundation/nav";
import type {
  MenuItems,
  SubmenuItem,
} from "@bloomberg/media-ui-nav/dist/types";
import type { TickerSecurity } from "@bloomberg/media-ui-markets/types/types";
import {
  FastPathScript,
  FortressProvider,
  FortressScript,
} from "@foundation/fortress";
import {
  FortressClientColorOptions,
  FortressClientDomain,
  type FortressOptions,
} from "@foundation/fortress/lib/types";
import { GtmScript, trackClickEvent } from "@foundation/nextjs-gtm";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useLocalStorage } from "@foundation/local-storage";
import { useIntl } from "react-intl";
import NavContainer from "~components/Nav";
import NavFooter from "~components/Footer";
import MetaTags, { MetaTagsProps } from "~components/MetaTags";
import PromiseResourceError from "~errors/PromiseResourceError";
import {
  getAdOptions,
  getContentMetadata,
  getSparkleProviderOptions,
} from "~utils/adUtils";
import { PageContext, PageContextType } from "~context/PageContext";
import { PlugScript } from "~components/Plug";
import type { AdConfig } from "~api/getAdConfig";

/**
 * DO NOT USE optionEnv for NEXT_PUBLIC variables, they're not being properly substituted by value
 */
const SEARCH_CLIENT_URL =
  process.env.NEXT_PUBLIC_SEARCH_CLIENT_URL ?? "https://search.bloomberg.com/";
const colorEnv = process.env.NEXT_PUBLIC_COLOR_ENV;
const fortressClientDomain =
  !process.env.NEXT_PUBLIC_COLOR_ENV ||
  ["production", "blue", "orange"].includes(process.env.NEXT_PUBLIC_COLOR_ENV)
    ? FortressClientDomain.PRODUCTION
    : FortressClientDomain.STAGING;

const getStatsigEnvTier = () => {
  if (
    typeof window !== "undefined" &&
    (window?.location.hostname.includes("localhost") ||
      window?.location.hostname.includes("local.bloomberg"))
  ) {
    return "development";
  } else if (process.env.NEXT_PUBLIC_COLOR_ENV === "production")
    return "production";

  return "staging";
};

// Create a client for Tanstack Query
const queryClient = new QueryClient();

export type PageWrapperProps = MetaTagsProps & {
  analytics: unknown;
  contentType: string;
  countryCode: string;
  disablePaywall?: boolean;
  errors: PromiseResourceError[];
  footerMenuItems: Record<string, SubmenuItem>;
  fortressOptions: FortressOptions;
  menuItems: MenuItems;
  partial: boolean;
  pageId: string;
  pageData: unknown;
  pageType: string;
  removeTickerBar?: boolean;
  tickerbar: TickerSecurity[];
  tophat: TophatSnippet["snippet"];
  adTargeting: Targeting;
  adConfig: AdConfig;
};

export function PageWrapper(props: React.PropsWithChildren<PageWrapperProps>) {
  const {
    analytics,
    children,
    contentType,
    countryCode,
    disablePaywall = false,
    footerMenuItems,
    fortressOptions,
    menuItems,
    metadata,
    partial,
    pageId,
    pageData = {},
    pageType,
    tickerbar,
    removeTickerBar = false,
    tophat,
    adConfig,
  } = props;

  const LOCAL_STORAGE_KEY = `page-preference-${pageId}`;
  const [pagePreference, setPagePreference] = useLocalStorage(
    LOCAL_STORAGE_KEY,
    {}
  );

  const updatePagePreference = useCallback(
    (storageKey: string, storageValue: unknown) => {
      setPagePreference((prevPreference: Record<string, unknown>) => {
        const parsedPreference = prevPreference || {};

        parsedPreference[storageKey] = storageValue;

        return { ...parsedPreference };
      });
    },
    [setPagePreference]
  );

  const statsigClient = useStatsigClientWebSetup({
    environmentTier: getStatsigEnvTier(),
    statsigClientKey: process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY!,
  });

  const { locale } = useIntl();

  const fortressOptionsWithLocale = useMemo(() => {
    return {
      ...fortressOptions,
      ...(fortressOptions.articleInfo
        ? {
            articleInfo: {
              ...fortressOptions.articleInfo,
              locale,
            },
          }
        : undefined),
    };
  }, [fortressOptions, locale]);

  if (partial) return null;

  if (!tickerbar && pageType === "watchlist") {
    trackClickEvent(
      "watchlistError",
      "Get Watchlist Tickerbar Failure",
      "error",
      "watchlist"
    );
  }

  if (
    !tophat ||
    !menuItems ||
    !footerMenuItems ||
    (!removeTickerBar && !tickerbar)
  ) {
    return <Error statusCode={500} />;
  }

  const adOptionsProviderProps: Omit<
    ComponentProps<typeof AdOptionsProvider>,
    "children"
  > = {
    adOptions: adConfig.globalAdOptions,
    articleMetadata: getContentMetadata(pageId, contentType),
  };

  const pageContext: PageContextType = {
    countryCode,
    pageId,
    pageData,
    pageType,
    partial,
    pagePreference,
    updatePagePreference,
    searchClientURL: SEARCH_CLIENT_URL,
  };

  const fortressProps = {
    options: fortressOptionsWithLocale,
    ...(disablePaywall && { inView: false }),
  };

  const { isDecoupled, adContextData } = getAdOptions(pageId);

  return (
    <>
      {/* Plug-in client integration*/}
      <PlugScript />
      <GtmScript analytics={analytics} />
      <FortressProvider {...fortressProps}>
        <RegUIClientProvider>
          <StatsigProvider client={statsigClient}>
            <WinSparkleProvider
              {...getSparkleProviderOptions(
                pageType,
                (analytics as { adcode?: string })?.adcode,
                isDecoupled,
                adContextData,
                props.adTargeting,
                locale
              )}
            >
              <MetaTags metadata={metadata} />
              <AdOptionsProvider {...adOptionsProviderProps}>
                <ContainerWithParallaxLeaderboard isDecoupled={isDecoupled}>
                  <Tophat snippet={tophat} />
                  <NavProvider>
                    <NavContainer
                      menuItems={menuItems}
                      countryCode={countryCode}
                      locale={locale}
                    />
                  </NavProvider>
                  {!removeTickerBar && (
                    <TickerBarWithPillWrapper tickerBar={tickerbar || []} />
                  )}
                  <QueryClientProvider client={queryClient}>
                    <PageContext.Provider value={pageContext}>
                      {children}
                    </PageContext.Provider>
                    {/* Tanstack Devtools, will not be enabled in prod */}
                    <ReactQueryDevtools initialIsOpen={false} />
                  </QueryClientProvider>
                  <FastPathScript
                    fortressClientDomain={fortressClientDomain}
                    color={colorEnv as FortressClientColorOptions | undefined}
                  />
                  <FortressScript
                    fortressClientDomain={fortressClientDomain}
                    color={colorEnv as FortressClientColorOptions | undefined}
                    esm={true}
                  />
                  <NavFooter
                    footerMenuItems={footerMenuItems}
                    locale={locale}
                  />
                </ContainerWithParallaxLeaderboard>
              </AdOptionsProvider>
            </WinSparkleProvider>
          </StatsigProvider>
        </RegUIClientProvider>
      </FortressProvider>
    </>
  );
}
